'use client';

import clsx from 'clsx';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { BasicBin, BasicCheck, BasicClose } from '../../../icons/basic';
import { DirectionsArrowLeft, DirectionsArrowRight } from '../../../icons/directions';
import { ButtonPrimary } from '../../buttons/ButtonPrimary';
import { IconButton } from '../../buttons/IconButton';
import {
  FullScreenDialog,
  FullScreenDialogClose,
  FullScreenDialogContent,
  FullScreenDialogTitle,
} from '../../dialog/FullScreenDialog';
import { Popup, PopupContent } from '../../popup/Popup';
import { Typography } from '../../typography/Typography';
import type { MaskedDocumentUploadTexts } from './maskedDocumentUploadTexts';
import { getFileKey } from './upload-shared/fileIdentity';
import { ImagePreview } from './upload-shared/ImagePreview';
import { RedactionEditor, type RedactionEditorHandle } from './upload-shared/RedactionEditor';
import type { RedactionRect } from './upload-shared/redactionRect';

function ZoomInIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className="size-6" fill="currentColor" aria-hidden>
      <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Zm-40-60v-80h-80v-80h80v-80h80v80h80v80h-80v80h-80Z" />
    </svg>
  );
}

function ZoomOutIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className="size-6" fill="currentColor" aria-hidden>
      <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400ZM280-540v-80h200v80H280Z" />
    </svg>
  );
}

export type BatchFile = {
  file: File;
  status: 'pending' | 'confirmed';
};

export type MaskingDialogMode = 'masking' | 'review';

type ThumbnailStripProps = {
  items: Array<{ file: File; status: 'pending' | 'confirmed' }>;
  activeIndex: number;
  onSelect: (index: number) => void;
  texts: MaskedDocumentUploadTexts;
};

function ThumbnailStrip({
items,
activeIndex,
onSelect,
texts
}: ThumbnailStripProps) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((item, index) => {
        const isActive = index === activeIndex;
        const isConfirmed = item.status === 'confirmed';

        return (
          <button
            key={getFileKey(item.file)}
            type="button"
            onClick={() => onSelect(index)}
            className={clsx(
              'relative size-16 min-w-16 shrink-0 overflow-hidden rounded-sm border-2 bg-upload-disabled',
              isActive ? 'border-input-activated' : 'border-transparent',
              !isConfirmed && 'border-dashed border-upload-enabled',
            )}
            aria-label={texts.thumbnailLabel({ index, confirmed: isConfirmed })}
            aria-current={isActive ? 'true' : undefined}
          >
            <ImagePreview
              file={item.file}
              className="size-full object-cover object-center"
            />
            {isConfirmed && (
              <span className="absolute bottom-0.5 right-0.5 flex size-5 items-center justify-center rounded-full bg-button-positive-enabled text-button-positive-enabled">
                <BasicCheck className="size-3" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function DefaultMaskingInstructions({
  texts,
  watermarkPurpose,
}: {
  texts: MaskedDocumentUploadTexts;
  watermarkPurpose?: string;
}) {
  if (texts.renderDefaultMaskingInstructions) {
    return texts.renderDefaultMaskingInstructions({
      watermarkPurpose,
      instructionsText: texts.maskingInstructionsText,
      instructionsWatermarkText: texts.maskingInstructionsWatermarkText,
    });
  }

  return (
    <Typography variant="content-body">
      {texts.maskingInstructionsText}
      {watermarkPurpose && (
        <>
          <br />
          {texts.maskingInstructionsWatermarkText.replace('{purpose}', watermarkPurpose)}
        </>
      )}
    </Typography>
  );
}

type MaskingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: MaskingDialogMode;
  batchFiles: BatchFile[];
  reviewFiles: File[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onConfirm: (rectangles: RedactionRect[]) => void;
  onConfirmAll: () => void;
  onDeleteReviewFile: (file: File) => void;
  onClose: () => void;
  maskingInstructions?: ReactNode;
  hasWatermark?: boolean;
  watermarkPurpose?: string;
  texts: MaskedDocumentUploadTexts;
};

export function MaskingDialog({
  open,
  onOpenChange,
  mode,
  batchFiles,
  reviewFiles,
  activeIndex,
  onActiveIndexChange,
  onConfirm,
  onConfirmAll,
  onDeleteReviewFile,
  onClose,
  maskingInstructions,
  hasWatermark = false,
  watermarkPurpose,
  texts,
}: MaskingDialogProps) {
  const isReviewMode = mode === 'review';
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [rectanglesMap, setRectanglesMap] = useState<Map<string, RedactionRect[]>>(new Map());
  const [selectedRectId, setSelectedRectId] = useState<string | null>(null);
  const [canZoomIn, setCanZoomIn] = useState(true);
  const [canZoomOut, setCanZoomOut] = useState(false);
  const redactionEditorRef = useRef<RedactionEditorHandle>(null);

  useEffect(() => {
    if (!open) {
      setShowFullscreen(false);
      setRectanglesMap(new Map());
      setSelectedRectId(null);
    }

    if (open && isReviewMode) {
      setShowFullscreen(true);
    }
  }, [open, isReviewMode]);

  useEffect(() => {
    if (!showFullscreen) {
      return;
    }

    const previousBodyTouchAction = document.body.style.touchAction;
    const previousHtmlTouchAction = document.documentElement.style.touchAction;

    document.body.style.touchAction = 'manipulation';
    document.documentElement.style.touchAction = 'manipulation';

    return () => {
      document.body.style.touchAction = previousBodyTouchAction;
      document.documentElement.style.touchAction = previousHtmlTouchAction;
    };
  }, [showFullscreen]);

  const stripItems = isReviewMode
    ? reviewFiles.map((file) => ({ file, status: 'confirmed' as const }))
    : batchFiles;

  const activeFile = isReviewMode ? reviewFiles[activeIndex] : batchFiles[activeIndex]?.file;
  const hasMultiple = stripItems.length > 1;
  const canGoPrevious = activeIndex > 0;
  const canGoNext = activeIndex < stripItems.length - 1;

  const activeFileKey = activeFile ? getFileKey(activeFile) : null;
  const activeRectangles = activeFileKey ? rectanglesMap.get(activeFileKey) ?? [] : [];

  const setActiveRectangles = (rects: RedactionRect[]) => {
    if (!activeFileKey) {
      return;
    }

    setRectanglesMap((current) => {
      const next = new Map(current);

      next.set(activeFileKey, rects);

      return next;
    });
  };

  const handlePrevious = () => {
    if (canGoPrevious) {
      setSelectedRectId(null);
      onActiveIndexChange(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      setSelectedRectId(null);
      onActiveIndexChange(activeIndex + 1);
    }
  };

  const handleThumbnailSelect = (index: number) => {
    setSelectedRectId(null);
    onActiveIndexChange(index);
  };

  const handleSkipEditing = () => {
    onConfirmAll();
  };

  const handleStartEditing = () => {
    setShowFullscreen(true);
  };

  const handleCloseIntroPopup = () => {
    onClose();
    onOpenChange(false);
  };

  const handleCloseFullscreen = (nextOpen: boolean) => {
    if (!nextOpen) {
      setShowFullscreen(false);
      onClose();
      onOpenChange(false);
    }
  };

  const introFiles = batchFiles.map((bf) => bf.file);

  const renderIntroPopupContent = () => (
    <div className="flex flex-col gap-4">
      <div className="space-y-4">
        <div className="flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {introFiles.map((file) => (
            <div
              key={getFileKey(file)}
              className="relative size-20 min-w-20 shrink-0 overflow-hidden rounded-sm bg-upload-disabled border-2 border-upload-enabled border-separate"
            >
              <ImagePreview
                file={file}
                className="size-full object-cover object-center"
              />
            </div>
          ))}
        </div>

        <div>
          <Typography variant="content-body-bold" className="mb-2">
            {texts.introSensitiveDataHeading}
          </Typography>
          <div>
            {maskingInstructions ?? (
              <DefaultMaskingInstructions
                texts={texts}
                watermarkPurpose={hasWatermark ? watermarkPurpose : undefined}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-3 flex-col sm:flex-row items-center sm:justify-between">
        <button
          type="button"
          onClick={handleSkipEditing}
          className="py-2 text-center underline underline-offset-4"
        >
          <Typography variant="label-regular" type="span">
            {texts.skipEditingButton}
          </Typography>
        </button>
        <ButtonPrimary icon={DirectionsArrowRight}>
          <button type="button" onClick={handleStartEditing}>
            {texts.startEditingButton}
          </button>
        </ButtonPrimary>
      </div>
    </div>
  );

  const renderReviewBottomBar = () => (
    <>
      {activeFile ? (
        <button
          type="button"
          onClick={() => onDeleteReviewFile(activeFile)}
          className="flex items-center gap-1 text-error"
        >
          <BasicBin className="size-5" />
          <Typography variant="label-regular" type="span" textColorClassname="text-error">
            {texts.rejectButton}
          </Typography>
        </button>
      ) : (
        <span />
      )}
      <ButtonPrimary icon={DirectionsArrowRight}>
        <button type="button" onClick={onClose}>
          {texts.closeButton}
        </button>
      </ButtonPrimary>
    </>
  );

  const renderMaskingBottomBar = () => (
    <>
      <div className="flex items-center gap-2">
        <IconButton
          icon={<ZoomInIcon />}
          label={texts.zoomInLabel}
          type="button"
          onClick={() => redactionEditorRef.current?.zoomIn()}
          disabled={!canZoomIn}
          className={clsx(!canZoomIn && 'pointer-events-none opacity-30')}
        />
        <IconButton
          icon={<ZoomOutIcon />}
          label={texts.zoomOutLabel}
          type="button"
          onClick={() => redactionEditorRef.current?.zoomOut()}
          disabled={!canZoomOut}
          className={clsx(!canZoomOut && 'pointer-events-none opacity-30')}
        />
      </div>
      <ButtonPrimary icon={DirectionsArrowRight}>
        <button type="button" onClick={() => onConfirm(activeRectangles)}>
          {texts.confirmButton}
        </button>
      </ButtonPrimary>
    </>
  );

  const showIntroPopup = open && !isReviewMode && !showFullscreen;

  return (
    <>
      {/* Small intro popup — shown first when adding new photos (masking mode only) */}
      {showIntroPopup && (
      <Popup open>
        <PopupContent
          title={texts.introPopupTitle}
          size="small"
          style={{
            height: 'auto',
            maxHeight: 'calc(100dvh - 2rem)',
            width: 'min(480px, calc(100vw - 2rem))',
          }}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          withCloseButton={false}
        >
          {renderIntroPopupContent()}
        </PopupContent>
      </Popup>
      )}

      {/* Fullscreen dialog for per-image confirm/reject + review mode */}
      {showFullscreen && (
      <FullScreenDialog open onOpenChange={handleCloseFullscreen}>
        <FullScreenDialogContent
          className="left-0 top-0 h-full max-h-dvh w-svw translate-x-0 translate-y-0 overflow-visible"
          style={{ padding: 0, touchAction: 'manipulation' }}
          onPointerDownOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <div className="flex h-full min-h-0 flex-col gap-0 overflow-visible p-3 sm:p-5">
          <FullScreenDialogTitle className="sr-only">
            {isReviewMode ? texts.reviewDialogTitle : texts.maskingDialogTitle}
          </FullScreenDialogTitle>

          {/* Header */}
          <div className="flex shrink-0 flex-col gap-1 pb-4">
            <div className="flex items-center justify-between">
              <Typography variant="title-block">
                {isReviewMode ? texts.reviewDialogTitle : texts.maskingDialogTitle}
              </Typography>
              <FullScreenDialogClose asChild>
                <IconButton
                  icon={<BasicClose />}
                  label={texts.closeButtonLabel}
                  type="button"
                  className="size-8 p-1"
                />
              </FullScreenDialogClose>
            </div>
            {!isReviewMode && (
              <Typography variant="content-body" textColorClassname="text-base-secondary-on-light">
                {texts.maskingEditorHint}
              </Typography>
            )}
          </div>

          {/* Image area */}
          <div className="flex min-h-0 flex-1 flex-col overflow-visible">
            {stripItems.length > 1 && (
              <ThumbnailStrip
                items={stripItems}
                activeIndex={activeIndex}
                onSelect={handleThumbnailSelect}
                texts={texts}
              />
            )}

            <div className="relative mt-4 flex min-h-0 flex-1 items-center justify-center overflow-visible -mx-3 sm:-mx-5">
              {hasMultiple && (
                <IconButton
                  icon={<DirectionsArrowLeft />}
                  label={texts.previousImageLabel}
                  type="button"
                  className={clsx(
                    'absolute left-0 z-10 hidden min-h-11 min-w-11 md:flex',
                    !canGoPrevious && 'pointer-events-none opacity-30',
                  )}
                  onClick={handlePrevious}
                  disabled={!canGoPrevious}
                />
              )}

              {activeFile && (
                <div className={clsx('flex h-full w-full items-center justify-center overflow-visible', hasMultiple && 'md:mx-12')}>
                  {isReviewMode ? (
                    <ImagePreview
                      file={activeFile}
                      className="max-h-full max-w-full object-contain object-center"
                    />
                  ) : (
                    <RedactionEditor
                      ref={redactionEditorRef}
                      file={activeFile}
                      rectangles={activeRectangles}
                      onRectanglesChange={(rects) => setActiveRectangles(rects)}
                      selectedRectId={selectedRectId}
                      onSelectedRectChange={setSelectedRectId}
                      onZoomStateChange={({
canZoomIn: nextCanZoomIn,
canZoomOut: nextCanZoomOut
}) => {
                        setCanZoomIn(nextCanZoomIn);
                        setCanZoomOut(nextCanZoomOut);
                      }}
                      texts={texts}
                    />
                  )}
                </div>
              )}

              {hasMultiple && (
                <IconButton
                  icon={<DirectionsArrowRight />}
                  label={texts.nextImageLabel}
                  type="button"
                  className={clsx(
                    'absolute right-0 z-10 hidden min-h-11 min-w-11 md:flex',
                    !canGoNext && 'pointer-events-none opacity-30',
                  )}
                  onClick={handleNext}
                  disabled={!canGoNext}
                />
              )}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="flex shrink-0 items-center justify-between pt-4 pb-[max(0px,calc(env(safe-area-inset-bottom)-1.25rem))]">
            {isReviewMode ? renderReviewBottomBar() : renderMaskingBottomBar()}
          </div>
          </div>
        </FullScreenDialogContent>
      </FullScreenDialog>
      )}
    </>
  );
}
